import React, { useEffect, useState } from "react";
import { getUsageLimits } from "../api";
import TopicImport from "./TopicImport";
import VideoTranscriptionTopicCreator from "./VideoTranscriptionTopicCreator";
import AITopicCreator from "./AITopicCreator";

/**
 * Create Topic modal (Word Bank style).
 *
 * - Normal users: a simplified YouTube-transcription flow, capped by the admin
 *   settings (duration + total-topics limit). Topics are private to the user.
 * - Admins: the full flow with a Standard / Video type picker.
 *
 * Props:
 *   isAdmin     – unlock the full admin flow
 *   ownedCount  – how many topics this user has already created (for the limit)
 *   onClose     – close the modal
 *   onCreated   – called after a topic is saved (parent reloads the list)
 */
export default function CreateTopicModal({ isAdmin = false, ownedCount = 0, onClose, onCreated }) {
  const [addTopicType, setAddTopicType] = useState("video");
  const [limits, setLimits] = useState(null);

  useEffect(() => {
    getUsageLimits()
      .then(setLimits)
      .catch(() => setLimits({}));
  }, []);

  const maxTopics = limits?.max_user_topics ?? null;
  const maxSeconds = limits?.user_video_max_seconds ?? 60;
  const atLimit = !isAdmin && maxTopics != null && ownedCount >= maxTopics;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
      onClick={onClose}
    >
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-ink-800">Create Topic</h2>
            <p className="text-xs text-ink-400">
              {isAdmin
                ? "Add a topic from a YouTube video, a template file, or an AI prompt. New topics are private until you publish them."
                : "Build your own topic from a YouTube video or a template file — only you can see it."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto scroll-elegant">
          {!isAdmin && maxTopics != null && (
            <div className="mb-3 text-xs text-ink-500">
              You've created <span className="font-semibold">{ownedCount}</span> of{" "}
              <span className="font-semibold">{maxTopics}</span> topics.
            </div>
          )}

          {atLimit ? (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              You've reached your limit of {maxTopics} topics. Delete one of your topics to create another.
            </div>
          ) : (
            <>
              <div className="mb-3">
                <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1">
                  Create from
                </label>
                <select
                  value={addTopicType}
                  onChange={(e) => setAddTopicType(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800"
                >
                  <option value="video">YouTube video</option>
                  <option value="standard">Import from template file (.txt)</option>
                  {isAdmin && <option value="ai">AI prompt (OpenAI)</option>}
                </select>
              </div>

              {addTopicType === "standard" && (
                <>
                  <div className="mb-3 text-xs text-ink-500">
                    New to the format?{" "}
                    <a
                      href={`${process.env.PUBLIC_URL || ""}/topic-template-example.txt`}
                      download="topic-template-example.txt"
                      className="font-medium text-emerald-700 underline hover:text-emerald-800"
                    >
                      Download an example template
                    </a>
                    , edit it, then upload your <code>.txt</code> below.
                  </div>
                  <TopicImport
                    onImported={async () => {
                      if (onCreated) await onCreated();
                      onClose();
                    }}
                  />
                </>
              )}

              {addTopicType === "ai" && isAdmin && (
                <AITopicCreator
                  onCreated={async () => {
                    if (onCreated) await onCreated();
                    onClose();
                  }}
                  onCancel={onClose}
                />
              )}

              {addTopicType === "video" && (
                <VideoTranscriptionTopicCreator
                  isAdmin={isAdmin}
                  maxDurationSeconds={maxSeconds}
                  onSaved={async () => {
                    if (onCreated) await onCreated();
                  }}
                  onCancel={onClose}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
